import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { UploadSimple, CheckCircle, XCircle, Clock } from '@phosphor-icons/react';

export default function Verification() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ name: user?.full_name || '', phone: '', country: '', broker_name: '', account_number: '', broker_uid: '', broker_email: user?.email || '', notes: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { (async () => { const { data } = await api.get('/verification/me'); setCurrent(data && data.id ? data : null); })(); }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error('Screenshot too large (max 2MB)');
    const r = new FileReader();
    r.onload = () => setScreenshot(r.result);
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!screenshot) return toast.error('Please upload a screenshot');
    setLoading(true);
    try {
      await api.post('/verification/submit', { ...form, screenshot_base64: screenshot });
      toast.success('Verification submitted. Reviewed within 24 hours.');
      await refresh();
      const { data } = await api.get('/verification/me'); setCurrent(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="verification-page">
      <div>
        <div className="data-label text-saffron">Broker Verification</div>
        <h1 className="font-heading text-3xl font-bold mt-1">Unlock Premium — Free</h1>
        <p className="text-muted-foreground mt-2">Open a live trading account with our partner brokers, submit your details below, and get instant Premium access upon approval (within 24 hours).</p>
      </div>

      {current && (
        <div className={`glass p-6 rounded-xl neon-border flex items-center gap-4 ${current.status === 'approved' ? 'border-green-500/40' : current.status === 'rejected' ? 'border-red-500/40' : 'border-saffron/40'}`}>
          {current.status === 'approved' ? <CheckCircle size={40} className="text-green-400" weight="fill" /> :
           current.status === 'rejected' ? <XCircle size={40} className="text-red-400" weight="fill" /> :
           <Clock size={40} className="text-saffron animate-pulse" weight="duotone" />}
          <div className="flex-1">
            <div className="font-heading font-bold text-lg capitalize">{current.status}</div>
            <div className="text-sm text-muted-foreground">
              {current.status === 'pending' && 'Your verification is under review. We typically respond within 24 hours.'}
              {current.status === 'approved' && 'Congratulations! Your premium access is active.'}
              {current.status === 'rejected' && (current.admin_notes || 'Please resubmit with correct details.')}
            </div>
          </div>
        </div>
      )}

      {(!current || current.status === 'rejected') && (
        <form onSubmit={submit} className="glass p-6 rounded-xl space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="data-label">Full Name</Label><Input data-testid="v-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
            <div><Label className="data-label">Phone</Label><Input data-testid="v-phone" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
            <div><Label className="data-label">Country</Label><Input data-testid="v-country" required value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
            <div><Label className="data-label">Broker Name</Label><Input data-testid="v-broker" required value={form.broker_name} onChange={e => setForm({...form, broker_name: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="e.g. Exness, IC Markets" /></div>
            <div><Label className="data-label">Account Number</Label><Input data-testid="v-account" required value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
            <div><Label className="data-label">Broker UID</Label><Input data-testid="v-uid" required value={form.broker_uid} onChange={e => setForm({...form, broker_uid: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
            <div className="md:col-span-2"><Label className="data-label">Broker Email</Label><Input data-testid="v-email" type="email" required value={form.broker_email} onChange={e => setForm({...form, broker_email: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
          </div>
          <div><Label className="data-label">Screenshot (account page / deposit) — max 2MB</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="v-file" />
            <Button type="button" variant="outline" onClick={() => fileRef.current.click()} className="mt-1 w-full border-white/10 bg-surface-base h-11 hover:bg-white/5">
              <UploadSimple size={18} className="mr-2" /> {screenshot ? 'Screenshot loaded ✓' : 'Upload Screenshot'}
            </Button>
            {screenshot && <img src={screenshot} alt="preview" className="mt-3 rounded-md max-h-40 border border-white/10" />}
          </div>
          <div><Label className="data-label">Notes (optional)</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 bg-surface-base border-white/10" rows={2} /></div>
          <Button type="submit" disabled={loading} data-testid="v-submit" className="w-full h-11 bg-saffron hover:bg-saffron-hover text-white shadow-glow-saffron">
            {loading ? 'Submitting…' : 'Submit for Verification'}
          </Button>
        </form>
      )}
    </div>
  );
}
